import { Box, Button, Img, Paper, Stack, Typography } from '@lingoda/ui';
import { styled } from '@lingoda/ui/theme';
import { CheckCircleIcon } from '@lingoda/ui/icons/lingodaIcons';
import { subscriptionsStorePath } from '@lingoda/router';
import { useSelector } from '@lingoda/hooks';
import { sectionNameSelector } from '@lingoda/auth';
import { trans } from '@lingoda/i18n';
import zoomScreensImage from './zoomScreens.png';

interface SubscribeUnlockBannerProps {
    withImage?: boolean;
}

export const SubscribeUnlockBanner = ({ withImage = true }: SubscribeUnlockBannerProps) => {
    const sectionName = useSelector(sectionNameSelector);

    const textItems = [
        trans(
            'subscribe-unlock-lessons',
            { language: trans(/* @ignore */ `section-name-${sectionName}`, {}, 'student-store') },
            'student-store',
        ),
        trans('subscribe-unlock-materials', {}, 'student-store'),
        trans('subscribe-unlock-feedback', {}, 'student-store'),
    ];

    return (
        <Paper variant="outlined">
            <BannerGradient>
                {withImage && (
                    <ImageBox>
                        <Img src={zoomScreensImage} width="100%" alt="zoom" />
                    </ImageBox>
                )}
                <Stack flexBasis={withImage ? '65%' : '100%'} spacing={3}>
                    <Typography
                        variant="h2"
                        sx={{ color: (theme) => theme.colors.text.inverse }}
                        textAlign={{ xs: 'center', md: 'left' }}
                    >
                        {trans(
                            'subscribe-unlock-title',
                            {
                                language: trans(
                                    /* @ignore */ `section-name-${sectionName}`,
                                    {},
                                    'student-store',
                                ),
                            },
                            'student-store',
                        )}
                    </Typography>
                    <Stack spacing={1.5}>
                        {textItems.map((message) => (
                            <Box key={message} display="flex" alignItems="center">
                                <Box
                                    component="span"
                                    alignSelf="flex-start"
                                    display="inline-flex"
                                    flexShrink={0}
                                    mr={2}
                                    sx={{ color: (theme) => theme.colors.text.inverse }}
                                >
                                    <CheckCircleIcon size={24} weight="fill" />
                                </Box>
                                <Typography
                                    variant="body1"
                                    sx={{ color: (theme) => theme.colors.text.inverse }}
                                    display="inline-flex"
                                >
                                    {message}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                    <Typography
                        variant="h3"
                        sx={{ color: (theme) => theme.colors.text.inverse }}
                        textAlign={{ xs: 'center', md: 'left' }}
                    >
                        {trans(
                            'subscribe-unlock-promotion',
                            {
                                language: trans(
                                    /* @ignore */ `section-name-${sectionName}`,
                                    {},
                                    'student-store',
                                ),
                            },
                            'student-store',
                        )}
                    </Typography>
                    <Box alignSelf={{ xs: 'stretch', md: 'flex-start' }}>
                        <Button
                            color="primary"
                            variant="contained"
                            inverted
                            href={subscriptionsStorePath()}
                            fullWidth
                        >
                            {trans('subscribe-unlock-subscribe-button', {}, 'student-store')}
                        </Button>
                    </Box>
                </Stack>
            </BannerGradient>
        </Paper>
    );
};

const BannerGradient = styled(Box)(({ theme }) => ({
    position: 'relative',
    background: theme.palette.primary.main,
    borderRadius: 'inherit',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
        flexDirection: 'row-reverse',
        padding: theme.spacing(6, 5, 6, 6),
    },
}));

const ImageBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
        flexBasis: '45%',
        marginBottom: 0,
        marginLeft: theme.spacing(1),
    },
}));
